import React from 'react';
import { connect } from 'react-redux';
import { List, ListItem, ListItemText, Collapse, Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import * as PIXI from 'pixi.js';
import { createActions } from '../../../store';
import CreatePageConstants from '../../../CreatePageConstants';
import WarningButton from '../../../../../buttons/WarningButton';
import BackdropDriftControls from './drift/BackdropDriftControls';
import BackdropSlowZoomSpeedSlider from './BackdropSlowZoomSpeedSlider';
import BackdropRumbleIntensitySlider from './BackdropRumbleIntensitySlider';
import BackdropMotionBlurSlider from './BackdropMotionBlurSlider';

const MOVEMENT = { DRIFT: 'drift', RUMBLE: 'rumble', ZOOM: 'zoom' };

const movementOptions = [
    { value: MOVEMENT.DRIFT, text: 'Drift' },
    { value: MOVEMENT.RUMBLE, text: 'Rumble' },
    { value: MOVEMENT.ZOOM, text: 'Zoom' }
];

const styles = theme => ({
    item: {
        background: theme.palette.action.hover,
    },
    collapse: {
        backgroundColor: theme.palette.background.paperDark
    },
    group: {
        width: '100%',
    },
    button: {
        flex: '1 0 auto',
        height: theme.spacing(4),
        color: 'rgba(255, 255, 255, 0.48)',
        background: 'rgba(0, 0, 0, 0.2)',
        border: '1px solid rgba(255, 255, 255, 0.23)',
        transition: theme.transitions.create('background', {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.shortest,
        })
    },
});

const BackdropMovementListItem = (props) => {
    const { classes, selected, updateBackgroundSettings } = props;

    // Drift / Rumble / Zoom are mutually exclusive, so enabling one disables the others.
    // A null value (deselecting the active button) leaves the background static.
    const onMovementChange = (_, value) => {
        updateBackgroundSettings({
            drift: { enabled: value === MOVEMENT.DRIFT },
            slowZoom: { enabled: value === MOVEMENT.ZOOM },
            shakeEnabled: value === MOVEMENT.RUMBLE,
            motionBlurEnabled: value === MOVEMENT.RUMBLE
        });
    };

    // Drift, Rumble and Zoom each expand extra controls below
    const expanded = selected === MOVEMENT.DRIFT || selected === MOVEMENT.RUMBLE || selected === MOVEMENT.ZOOM;
    const rumbleWarning = selected === MOVEMENT.RUMBLE && !PIXI.utils.isWebGLSupported()
        ? CreatePageConstants.LIMITED_VISIBILITY_WARNING_TEXT
        : null;

    return (
        <>
            <ListItem
                aria-expanded={expanded}
                className={expanded ? classes.item : null}
            >
                <ListItemText>
                    Movement
                    {rumbleWarning && <WarningButton title={rumbleWarning} />}
                </ListItemText>
                <Box>
                    <ToggleButtonGroup
                        value={selected}
                        exclusive
                        onChange={onMovementChange}
                        size='small'
                        className={classes.group}
                    >
                        {movementOptions.map((option) =>
                            <ToggleButton
                                value={option.value}
                                key={option.value}
                                className={classes.button}
                            >
                                {option.text}
                            </ToggleButton>
                        )}
                    </ToggleButtonGroup>
                </Box>
            </ListItem>

            <Collapse in={selected === MOVEMENT.DRIFT} timeout='auto' unmountOnExit className={classes.collapse}>
                <List dense disablePadding>
                    <BackdropDriftControls />
                </List>
            </Collapse>

            <Collapse in={selected === MOVEMENT.RUMBLE} timeout='auto' unmountOnExit className={classes.collapse}>
                <List dense disablePadding>
                    <BackdropRumbleIntensitySlider />
                    <BackdropMotionBlurSlider />
                </List>
            </Collapse>

            <Collapse in={selected === MOVEMENT.ZOOM} timeout='auto' unmountOnExit className={classes.collapse}>
                <List dense disablePadding>
                    <BackdropSlowZoomSpeedSlider />
                </List>
            </Collapse>
        </>
    );
};

const mapStateToProps = (state) => {
    const { drift, slowZoom, shakeEnabled } = state.createPreview.present.videoSettings.background;
    const selected = drift?.enabled
        ? MOVEMENT.DRIFT
        : shakeEnabled
            ? MOVEMENT.RUMBLE
            : slowZoom?.enabled
                ? MOVEMENT.ZOOM
                : null;
    return { selected };
};

const mapDispatchToProps = (dispatch) => ({
    updateBackgroundSettings: (properties) => dispatch(createActions.updateBackgroundSettings(properties))
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(BackdropMovementListItem, styles));
